import { IndicatorIcon } from '@vizit/icons';
import * as React from 'react';
import {
  CHART_COLOR_AMBER,
  CHART_COLOR_BG_TRANSPARENT,
  CHART_COLOR_DARK_RED,
  CHART_COLOR_GRAY,
  CHART_COLOR_GREEN,
  CHART_COLOR_INDICATOR,
  CHART_COLOR_RED,
  CHART_COLOR_YELLOW_GREEN,
} from './colors';
import type { AreaMetric } from './types';
import { clamp, lerp } from './utils';

interface AreaChartProps {
  metric: AreaMetric;
  /**
   * Width of the chart
   * @default 300
   */
  width?: number;
  /**
   * Height of the chart
   * @default 60
   */
  height?: number;
}

// score-thresholds-allow-file: chart geometry (px, sample count) and the 5-tier colour scale
const PAD_L = 4;
const PAD_R = 4;
const PAD_T = 6;
const PAD_B = 6;

/**
 * Renders an area chart showing score position with color-coded regions
 * Colors match ScoreDiagram ranges: dark red (0-20), red (20-40), amber (40-60), yellow-green (60-80), green (80-100)
 */
const AreaChart = React.forwardRef<SVGSVGElement, AreaChartProps>((props, forwardedRef) => {
  const { metric, width = 300, height = 60 } = props;
  const { scoreRange, curve, currentValue } = metric;

  const CW = width - PAD_L - PAD_R;
  const CH = height - PAD_T - PAD_B;

  const toSvgX = (normX: number): number => PAD_L + normX * CW;
  const toSvgY = (normY: number): number => PAD_T + (1 - normY) * CH;

  const t = clamp((currentValue - scoreRange.min) / (scoreRange.max - scoreRange.min), 0, 1);

  const N = 100;
  const baseY = toSvgY(0);

  // Full curve points (gray background)
  const fullPoints: string[] = [];
  for (let i = 0; i <= N; i++) {
    const nx = i / N;
    const ny = lerp(nx, curve);
    fullPoints.push(`${toSvgX(nx)},${toSvgY(ny)}`);
  }
  const fullPoly = [`${toSvgX(0)},${baseY}`, ...fullPoints, `${toSvgX(1)},${baseY}`].join(' ');

  // Highlighted portion up to t
  const highlightSteps = Math.max(1, Math.round(t * N));
  const hlPoints: string[] = [];
  for (let i = 0; i <= highlightSteps; i++) {
    const nx = (i / highlightSteps) * t;
    const ny = lerp(nx, curve);
    hlPoints.push(`${toSvgX(nx)},${toSvgY(ny)}`);
  }
  const hlPoly = [`${toSvgX(0)},${baseY}`, ...hlPoints, `${toSvgX(t)},${baseY}`].join(' ');

  // Determine color based on score value (matches ScoreDiagram ranges)
  let fillColor: string;
  if (currentValue < 20) {
    fillColor = CHART_COLOR_DARK_RED;
  } else if (currentValue < 40) {
    fillColor = CHART_COLOR_RED;
  } else if (currentValue < 60) {
    fillColor = CHART_COLOR_AMBER;
  } else if (currentValue < 80) {
    fillColor = CHART_COLOR_YELLOW_GREEN;
  } else {
    fillColor = CHART_COLOR_GREEN;
  }

  const indicatorX = toSvgX(t);
  const indicatorY = toSvgY(lerp(t, curve));

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      ref={forwardedRef}
      className="w-full h-auto"
      aria-label={`Area chart showing current value ${currentValue}`}
    >
      <rect width={width} height={height} rx="6" fill={CHART_COLOR_BG_TRANSPARENT} />
      <polygon points={fullPoly} fill={CHART_COLOR_GRAY} />
      <polygon points={hlPoly} fill={fillColor} opacity="0.8" />
      {/* Current value indicator */}
      <g transform={`translate(${indicatorX - 2}, ${indicatorY - 10})`}>
        <IndicatorIcon size={10} color={CHART_COLOR_INDICATOR} />
      </g>
    </svg>
  );
});

export type { AreaChartProps };
export { AreaChart };
